import React, {Component} from 'react';
import i18n from 'i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import queryparse from 'tricks/string/queryparse';
import {Trans} from 'react-i18next';
import track from 'utils/track';
import store from 'utils/store';
import {addHeapTrackingIfRequired} from 'utils/heapTracking';
import {addMindsetAIIfRequired} from 'utils/mindsetAI';

/* Utils */
import withAuth from '../../contexts/auth/withAuth';

/* Actions */

import LoginActions from '../../../actions/loginActions';

/* Components */

import Button from 'components/Button';
import Meter from 'components/Meter';
import Panel from 'components/Panel';
import Status from 'components/Status';

import FormField from 'components/FormField';
import PasswordInput from 'components/PasswordInput';

import ModalHeadline from 'components/ModalHeadline';

import AuthPageWrapper from './shared/AuthPageWrapper';

const pwStrengthTranslations = [
	'password_strength_weak',
	'password_strength_could_be_better',
	'password_strength_okay',
	'password_strength_good',
	'password_strength_strong',
];

const PW_STRENGTH_LABEL_ID = 'password-strength-label';

class ChangePassword extends Component {
	constructor(props) {
		super(props);

		const {
			invite_token,
			// This fallback for when there's no email query parameter is flawed,
			// as it will only work once, but not after a page refresh, ue to the
			// `LoginActions.logout()` further down clearing `authUser`
			email = props.authUser?.username,
			fv: isFirstVisit,
			returnTo,
		} = queryparse(props.location.search);

		this.state = {
			alert: '',
			invite_token,
			email,
			isFirstVisit: isFirstVisit && invite_token,
			returnTo,
		};

		this.setPassword = this.setPassword.bind(this);
		this.onClose = this.onClose.bind(this);
		this.inputRef = this.inputRef.bind(this);
		this.onChange = this.onChange.bind(this);

		// Remove existing sessions
		// Clearing sessions here does not seem to prevent an existing session from instantiating itself
		// The result is the same regardless of this change
		LoginActions.logout().then(() => store.clear());

		import(/* webpackChunkName: "zxcvbn" */ 'zxcvbn').then(
			m => (this.zxcvbn = m.default)
		);
	}

	setPassword(e) {
		e.preventDefault();

		this.setState({
			alert: '',
			alertType: 'fail',
		});

		const {invite_token} = this.state;

		const password = this.inputPassword.value;

		const opts = {invite_token};

		const changePassword = LoginActions.changePassword({password}, opts);

		changePassword
			.then(data => {
				track('user', 'logged-in', 'password-change');
				this.props.setAuthUser(data);

				// add Heap Analytics and Mindset
				const {id: userId, domain, domainId, isAdmin, role} = data;
				addHeapTrackingIfRequired({
					userId,
					domain,
					domainId,
					isAdmin,
					role,
				});
				addMindsetAIIfRequired();
			})
			.catch(() => {
				this.setState({
					alert: this.props.t('change_pw_fail'),
					alertType: 'fail',
				});
			});
	}

	onClose() {
		const {returnTo: returnUrl} = queryparse(this.props.location.search);
		this.props.history.push(returnUrl);
	}

	inputRef(el) {
		this.inputPassword = el;
	}

	onChange(e) {
		const password = e.target.value;

		this.setState({alert: ''});

		if (password.length < 1) {
			this.setState({
				passwordStrength: null,
			});
		} else if (password.length < 8) {
			this.setState({
				passwordStrength: 0,
			});
		} else if (this.zxcvbn) {
			const resp = this.zxcvbn(password);
			this.setState({
				passwordStrength: resp.score,
			});
		}
	}

	render() {
		const {
			passwordStrength,
			alert,
			alertType,
			email,
			isFirstVisit,
			returnTo,
		} = this.state;

		let onClose;
		if (returnTo) {
			onClose = this.onClose;
		}

		return (
			<AuthPageWrapper>
				<form onSubmit={this.setPassword}>
					<ModalHeadline
						title={
							isFirstVisit
								? i18n.t('change_pw_title_initial')
								: i18n.t('change_pw_title')
						}
						onClose={onClose}
					/>
					<Panel>
						{/* prettier-ignore */}
						<Trans
							i18nKey="change_pw_description"
							component="p"
							email={email}
						>
							Please choose a new password for your account <strong>{email}</strong>
						</Trans>
						<p>{i18n.t('change_pw_rules')}</p>
					</Panel>
					<Panel>
						<FormField
							label={i18n.t('new_password')}
							alert={alert}
							alertType={alertType}
						>
							<PasswordInput
								inputRef={this.inputRef}
								onChange={this.onChange}
								autoComplete="new-password"
							/>
							<Status
								dimmed
								display="block"
								fontSize="s"
								mt="xs"
								id={PW_STRENGTH_LABEL_ID}
							>
								{i18n.t('password_strength')}:{' '}
								{passwordStrength !== null
									? i18n.t(
											pwStrengthTranslations[
												passwordStrength
											]
										)
									: '-'}
							</Status>
							<Meter
								visualMin={0.5}
								min={0}
								low={1}
								high={3}
								max={4}
								optimum={4}
								value={passwordStrength}
								id="password-strength"
								aria-labelledby={PW_STRENGTH_LABEL_ID}
							/>
						</FormField>
					</Panel>

					<Panel align="center">
						<Button
							id="change_set_password_submit_button"
							type="submit"
							variant="primary"
						>
							{isFirstVisit
								? i18n.t('change_pw_submit_initial')
								: i18n.t('change_pw_submit')}
						</Button>
					</Panel>
				</form>
			</AuthPageWrapper>
		);
	}
}

function ChangePasswordWithRouter(props) {
	const location = useLocation();
	const navigate = useNavigate();

	const history = {
		push: (to, state) => navigate(to, {state}),
		replace: (to, state) => navigate(to, {replace: true, state}),
	};

	return <ChangePassword {...props} location={location} history={history} />;
}

export default withAuth(ChangePasswordWithRouter);
